import * as React from 'react';
import {createContext, ReactNode, Suspense, useEffect, useState} from 'react';
import * as emotion from 'emotion';
import {BaseComponent, BrowserHistoryModule, OnUnauthenticatedResponse, RoutingModule, Tooltip, WrapperProps} from "@intuitionrobotics/thunderstorm/frontend";
import {OnRequestListener} from "@intuitionrobotics/thunderstorm";
import {createTheme} from "@mui/material";
import {registerRoutes, Route_Home, Route_Monitoring} from "./routes";
import Loader from "@components/Loader";
import {BadImplementationException} from "@intuitionrobotics/ts-common";
import Dialog_Confirmation from "@components/Dialog_Confirmation";
import {AccountModule, LoggedStatus, OnLoginStatusUpdated, StorageKey_UserEmail} from "@intuitionrobotics/user-account/frontend";
import {EnvironmentModule} from '@modules/EnvironmentModule';
import {QueryParam_RedirectUrl} from "@intuitionrobotics/user-account";
import {MyToaster} from "./renderers/MyToaster";
import {BugReport} from './BugReport';
import {Dialog} from './Dialog';
import {ThemeProvider} from "@mui/material/styles";
import {DataEnvsModule, UiEnvItem} from "@modules/DataEnvsModule";
import {ActivationsModule, RequestKey_DeleteActivations, RequestKey_ListActivations} from "@modules/ActivationsModule";
import {DataEnvItem} from "@app/ir-q-app-common/types/units";
import DrawerAppBar from "./MenuWithDrawer";
import {SimpleLoader} from "@components/SimpleLoader";
import {PermissionLevels} from '@app-sp/app-shared/shared';
import {extractPermissions, getUserTokenInfo} from "@consts/utils";
import {RequestKey_QueryUnitMetadata, UnitsModule} from "@modules/UnitsModule";
import {useLocation} from "react-router-dom";
import {VersionUpdate} from "@components/version-update/VersionUpdate";
import {ErrorBoundary} from "./ErrorBoundary";

// Executes a render thunk as a child component, so any synchronous throw inside
// it (the logged-status switch, registerRoutes()'s JWT decode, deep page
// renders) happens UNDER the ErrorBoundary and is caught, rather than during
// App.render() itself where it would blank the whole app.
const AppContent = ({render}: { render: () => React.ReactNode }) => <>{render()}</>;

// True on phone-sized viewports — used to make Monitoring the default landing
// on a phone (it renders its own mobile layout below this breakpoint).
const isMobileViewport = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

// Send the default URL to /monitoring once per page load on mobile, so an
// explicit Home visit later in the session sticks instead of bouncing back.
let didMobileHomeRedirect = false;

const theme = createTheme({});

export const dropdownClassName = emotion.css({
    position: "relative",
    padding: 6
})

export function getCurrentUserEmail() {
    return StorageKey_UserEmail.get("fe-stub");
}

export const UnitContext: React.Context<{ activatedUnits: Set<string>, activationsLoaded: boolean }> =
    createContext<{ activatedUnits: Set<string>, activationsLoaded: boolean }>({activatedUnits: new Set<string>(), activationsLoaded: false});

export enum UserGroupAction {
    CREATE,
    DELETE,
    UPDATE
}


export type UserGroupReducerAction = { type: UserGroupAction, payload: DataEnvItem };
export const userGroupReducer = async (action: UserGroupReducerAction, state: DataEnvItem[]): Promise<DataEnvItem[]> => {
    switch (action.type) {
        case UserGroupAction.CREATE:
            const newlyCreated = await DataEnvsModule.createSync(action.payload);
            return [...state, newlyCreated];
        case UserGroupAction.UPDATE:
            const found = state.findIndex(({_id}) => _id === action.payload._id);
            if (found === -1)
                return state;
            await DataEnvsModule.createSync(action.payload);
            state[found] = action.payload;
            return state;
        case UserGroupAction.DELETE:
            await DataEnvsModule.deleteSync(action.payload._id);
            return state.filter(({_id}) => _id !== action.payload._id);
        default:
            return state;
    }
}

type UserGroupsContextType = { userGroups: UiEnvItem[], dispatch: (type: UserGroupAction, payload: UiEnvItem) => Promise<void> }
export const UserGroupsContext: React.Context<UserGroupsContextType> = createContext<UserGroupsContextType>({
    userGroups: [],
    dispatch: async () => {
    }
});


type UserPermissionContextType = {
    userPermission?: PermissionLevels
}
export const UserPermissionContext: React.Context<UserPermissionContextType> = createContext({});

type LoadingContextType = { setLoading: (loading: boolean) => void }
export const LoadingContext = createContext<LoadingContextType>({
    setLoading: () => {
    }
});
const AppProviders = ({children}: { children: ReactNode }) => {
    const [state, setState] = useState<{ userGroups: UiEnvItem[], error: unknown }>({userGroups: [], error: undefined});
    const [loading, setLoading] = useState(false)
    const [permissions, setPermissions] = useState<PermissionLevels | undefined>();
    const location = useLocation();

    const dispatch = async (type: UserGroupAction, payload: UiEnvItem) => {
        const result = userGroupReducer({type, payload}, state.userGroups);
        try {
            const newState = await result;
            setState({userGroups: newState, error: undefined});
        } catch (err) {
            setState({...state, error: err});
        }
    };

    useEffect(() => {
        if (location.pathname === "/") {
            // On the first "/" load on a phone, make Monitoring the default —
            // but only if the user has access (the route is permission-gated)
            // and only once, so navigating Home later doesn't trap them.
            if (!didMobileHomeRedirect && isMobileViewport() && RoutingModule.isRouteRegistered(Route_Monitoring)) {
                didMobileHomeRedirect = true;
                RoutingModule.goToRoute(Route_Monitoring);
            }
            return;
        }

        const myRouteKey = RoutingModule.getMyRouteKey();
        if (myRouteKey && RoutingModule.isRouteRegistered(Route_Home) && !RoutingModule.isRouteRegistered(myRouteKey)) {
            RoutingModule.goToRoute(Route_Home)
        }
    }, [location.key]);

    useEffect(() => {
        (async () => {
            // Offline: the data-env collection isn't served by the offline backend
            // (db query returns {}), so use the committed fixtures instead of an XHR
            // — otherwise userGroups is non-array and Page_Units crashes on envs.*.
            // Dynamically imported so the offline fixtures stay code-split out of the
            // online bundle (this branch never runs online).
            const dataEnvs: UiEnvItem[] = EnvironmentModule.isOffline()
                ? (await import("./pages/units/offline/fixtures")).offlineEnvs
                : await DataEnvsModule.querySync();
            const permissionLevels = extractPermissions(getUserTokenInfo());
            setPermissions(permissionLevels);


            setState({userGroups: dataEnvs, error: undefined});
        })().catch(console.error)
    }, []);

    useEffect(() => {
        if (!state.userGroups?.length)
            return;

        UnitsModule.fetchPairedUnitsImpl(state.userGroups);
    }, [state.userGroups]);

    return <UserPermissionContext.Provider value={{userPermission: permissions}}>
        <UserGroupsContext.Provider value={{userGroups: state.userGroups, dispatch}}>
            <LoadingContext.Provider value={{setLoading}}>
                {children}
                {loading && <SimpleLoader overlay={true}/>}
            </LoadingContext.Provider>
        </UserGroupsContext.Provider>
    </UserPermissionContext.Provider>
}

type AppState = {
    showDropdown: boolean,
    activatedUnits: Set<string>,
    // Flips true once the activations list has been fetched this session. The
    // monitoring table renders from activatedUnits ∩ rs-view, so the page keeps
    // its loader up until this is set — otherwise it briefly shows an empty/partial
    // table from the cached activation ids while the real list is still loading.
    activationsLoaded: boolean,
    error?: unknown
}

export class App
    extends BaseComponent<WrapperProps, AppState>
    implements OnUnauthenticatedResponse, OnLoginStatusUpdated, OnRequestListener {

    private Key_ActivationIds = "activationIds";

    constructor(p: WrapperProps) {
        super(p);
        this.logInfo(`App started, version: ${process.env.appVersion}`);

        // Guard the cached-ids read: a corrupt/partial localStorage value must
        // not throw in the constructor (that blanks the app before any boundary
        // can catch it).
        let activatedUnits: string[] = [];
        try {
            const cachedActivationIds = localStorage.getItem(this.Key_ActivationIds);
            if (cachedActivationIds) {
                const parsed = JSON.parse(cachedActivationIds);
                if (Array.isArray(parsed))
                    activatedUnits = parsed;
            }
        } catch (e) {
            this.logWarning(`Ignoring corrupt ${this.Key_ActivationIds} in localStorage`, e as Error);
        }
        this.state = {
            showDropdown: true,
            activatedUnits: new Set(activatedUnits),
            activationsLoaded: false,
            error: undefined
        }
    }

    __onRequestCompleted = (key: string, success: boolean) => {
        if (!success)
            return;

        if (key === RequestKey_ListActivations || key === RequestKey_DeleteActivations) {
            const unitIds = ActivationsModule.getActivatedUnitIds();
            localStorage.setItem(this.Key_ActivationIds, JSON.stringify(unitIds));

            return this.setState({activatedUnits: new Set(unitIds), activationsLoaded: true})
        }

        if (key === RequestKey_QueryUnitMetadata) {
            const unitIds = ActivationsModule.getActivatedUnitIds();
            // QueryUnitMetadata is a metadata refresh, not the activations source
            // (that's RequestKey_ListActivations). The monitoring page triggers it on
            // mount; if it completes before the activations have loaded,
            // getActivatedUnitIds() is empty. Don't clobber a populated set with [] —
            // otherwise the page's `activatedUnits.size === 0` gate blanks mid-load.
            if (unitIds.length === 0)
                return;
            localStorage.setItem(this.Key_ActivationIds, JSON.stringify(unitIds));

            return this.setState({activatedUnits: new Set(unitIds), activationsLoaded: true})
        }
    }


    componentDidMount() {
        BrowserHistoryModule.getHistory().listen(location => {
            if (location.pathname === "/")
                return this.setState({showDropdown: false});

            this.setState({showDropdown: true})
        })
    }

    onLoginStatusUpdated = () => {

        this.forceUpdate();
    };

    onUnauthenticatedResponse = () => {
        AccountModule.logout();
    };

    render() {
        return <ErrorBoundary>
            <AppContent render={this.renderContent}/>
        </ErrorBoundary>;
    }

    private renderContent = () => {
        const status = AccountModule.getLoggedStatus();

        switch (status) {
            case LoggedStatus.LOGGED_IN:
                return <AppProviders>
                    <UnitContext.Provider value={{activatedUnits: this.state.activatedUnits, activationsLoaded: this.state.activationsLoaded}}>
                        {this.renderApp()}
                    </UnitContext.Provider>
                </AppProviders>

            case LoggedStatus.LOGGED_OUT:
                return this.renderRedirect();

            case LoggedStatus.VALIDATING:
                return this.renderValidating();

            default:
                throw new BadImplementationException(`logged status can only be one of 'LOGGED_IN', 'LOGGED_OUT' or 'VALIDATING'`);
        }
    }

    renderRedirect = () => {
        const encodedOrigin = encodeURIComponent(EnvironmentModule.getOrigin());
        window.location.href = `${EnvironmentModule.getKasperoFeUrl()}?${QueryParam_RedirectUrl}=${encodedOrigin}`;
        return null;
    };

    private renderApp() {
        registerRoutes();

        return <ThemeProvider theme={theme}>
            <div className={`match_width match_height`}>
                {this.getApp()}
                <Dialog_Confirmation/>
                <MyToaster/>
                <Dialog/>
                <Tooltip/>
                <BugReport/>
                <VersionUpdate/>
            </div>
        </ThemeProvider>
    }

    private getApp = () => <DrawerAppBar
        showUnitSelection={this.state.showDropdown}
    >
        <Suspense fallback={<SimpleLoader/>}>
            {RoutingModule.getRoutesMap()}
        </Suspense>
    </DrawerAppBar>;

    private renderValidating = () => <>
        <Loader/>
        <MyToaster/>
    </>;
}
